import { isSafari, isWeb } from "@helpers/platform";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { useToast } from "@memorang/ui";
// import { setAudioModeAsync } from "expo-audio";
import { Audio } from "expo-av";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWebMetering } from "../containers/AudioRecorderContainer/hooks/useWebMetering";

const MAX_RETRY = 3;

const normalizeMetering = (metering: number) => {
	const minDb = -60;
	const maxDb = -10;
	const clampedDb = Math.min(maxDb, Math.max(minDb, metering));
	return ((clampedDb - minDb) / (maxDb - minDb)) ** 2;
};

type Props = {
	onRecordingComplete?: (uri: string) => void;
};

const useRecorder = ({ onRecordingComplete }: Props) => {
	const [isRecording, setIsRecording] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [meterHistory, setMeterHistory] = useState<number[]>([]);
	const [isError, setIsError] = useState(false);
	const [permissionResponse, requestPermission] = Audio.usePermissions();
	const { showToast } = useToast();
	const recordingRef = useRef<Audio.Recording | undefined>(undefined);

	const {
		meterHistory: webMeterHistory,
		startMetering: startWebMetering,
		startRecording: startWebRecording,
		stopMetering: stopWebMetering,
		stopRecording: stopWebRecording,
		clearMeterHistory: clearWebMeterHistory,
		audioChunks,
		clearAudioChunks: clearWebAudioChunks,
	} = useWebMetering(500, 2000);

	const startRecording = useCallback(
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		async (retryCount: number) => {
			try {
				if (permissionResponse?.status !== "granted") {
					const permissionResponse = await requestPermission();
					if (permissionResponse?.status !== "granted") {
						throw new Error("Permission not granted.");
					}
				}
				await Audio.setAudioModeAsync({
					allowsRecordingIOS: true,
					playsInSilentModeIOS: true,
					staysActiveInBackground: true,
				});
				// await setAudioModeAsync({
				// 	allowsRecording: true,
				// 	playsInSilentMode: true,
				// });
				const { recording } = await Audio.Recording.createAsync(
					{
						...Audio.RecordingOptionsPresets.HIGH_QUALITY,
						web: {
							...Audio.RecordingOptionsPresets.HIGH_QUALITY.web,
							mimeType: isSafari ? "audio/mp4" : "audio/webm",
						},
					},
					(status) => {
						setCurrentTime(status.durationMillis || 0);
						setDuration(status.durationMillis || 0);
						setIsRecording(status.isRecording);
						if (status.metering) {
							const normalizedLevel = normalizeMetering(status.metering);
							setMeterHistory((prev) => [...prev, normalizedLevel]);
						}
					},
				);
				await startWebMetering();

				if (isWeb) {
					await startWebRecording();
				}
				setIsError(false);
				setIsRecording(true);
				setMeterHistory([]);
				recordingRef.current = recording;
			} catch (error) {
				console.error("Failed to start recording", error);
				if (retryCount < MAX_RETRY) {
					await new Promise((resolve) => setTimeout(resolve, 200));
					await startRecording(retryCount + 1);
					return;
				}
				captureErrorToSentry(new Error("Failed to start recording"), {
					error: JSON.stringify(error),
				});
				setIsError(true);
				showToast({
					message:
						error instanceof Error
							? error.message
							: "Failed to start recording.",
				});
			}
		},
		[
			requestPermission,
			permissionResponse,
			showToast,
			startWebMetering,
			startWebRecording,
		],
	);

	const stopRecording = useCallback(async () => {
		stopWebMetering();
		stopWebRecording();
		await recordingRef.current?.stopAndUnloadAsync();
		const uri = recordingRef.current?.getURI();
		setIsRecording(false);
		setCurrentTime(0);
		await Audio.setAudioModeAsync({
			allowsRecordingIOS: false,
			playsInSilentModeIOS: true,
		});
		if (uri) {
			onRecordingComplete?.(uri);
		}
		recordingRef.current = undefined;
	}, [onRecordingComplete, stopWebMetering, stopWebRecording]);

	const clearMeterHistory = useCallback(() => {
		setMeterHistory([]);
		clearWebMeterHistory();
		clearWebAudioChunks();
	}, [clearWebMeterHistory, clearWebAudioChunks]);

	const unloadRecording = useCallback(async () => {
		if (recordingRef.current) {
			await recordingRef.current.stopAndUnloadAsync();
			await stopWebMetering();
			recordingRef.current = undefined;
		}
	}, [stopWebMetering]);

	useEffect(() => {
		return () => {
			unloadRecording();
		};
	}, [unloadRecording]);

	const value = useMemo(() => {
		return {
			isRecording,
			currentTime,
			duration,
			meterHistory: isWeb ? webMeterHistory : meterHistory,
			isError,
			startRecording,
			stopRecording,
			clearMeterHistory,
			unloadRecording,
			audioChunks,
			clearWebAudioChunks,
		};
	}, [
		isRecording,
		currentTime,
		duration,
		meterHistory,
		webMeterHistory,
		isError,
		startRecording,
		stopRecording,
		clearMeterHistory,
		unloadRecording,
		audioChunks,
		clearWebAudioChunks,
	]);

	return value;
};

export default useRecorder;
